import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, DollarSign, Target, Calendar, Users, Trophy, 
  Clock, Plus, TrendingUp
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [participation, setParticipation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [contributionNotes, setContributionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [householdId, setHouseholdId] = useState(null);

  useEffect(() => {
    fetchCampaign();
    fetchHouseholdId();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/fundraising/campaigns/${campaignId}`,
        { withCredentials: true }
      );
      setCampaign(response.data.campaign);
      setContributions(response.data.contributions);
      setParticipation(response.data.participation);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHouseholdId = async () => {
    if (user?.role === 'parent') {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard/parent`, { withCredentials: true });
        setHouseholdId(response.data.household_id);
      } catch (error) {
        console.error('Failed to fetch household:', error);
      }
    }
  };

  const handleContribute = async (e) => {
    e.preventDefault();
    if (!contributionAmount || !householdId) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/fundraising/contributions`,
        {
          campaign_id: campaignId,
          household_id: householdId,
          amount: parseFloat(contributionAmount),
          contribution_type: 'fundraiser',
          notes: contributionNotes
        },
        { withCredentials: true }
      );
      setShowContributeModal(false);
      setContributionAmount('');
      setContributionNotes('');
      fetchCampaign();
    } catch (error) {
      alert('Failed to record contribution');
    } finally {
      setSubmitting(false);
    }
  };

  const getCampaignTypeIcon = (type) => {
    const icons = {
      popcorn_sales: '🍿',
      cookie_sales: '🍪',
      bingo_night: '🎱',
      shake_helmet: '🪖',
      restaurant_night: '🍕',
      merchandise: '👕',
      donation_drive: '💝',
      sponsor: '🤝',
      other: '💰'
    };
    return icons[type] || icons.other;
  };

  const isAdmin = user?.role === 'league_admin' || user?.role === 'super_admin';

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (!campaign) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Campaign not found</p>
          <Button onClick={() => navigate('/fundraising')} className="mt-4">
            Back to Fundraising
          </Button>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="campaign-detail">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/fundraising')}
          className="flex items-center text-gray-600 hover:text-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fundraising
        </button>

        {/* Campaign Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="text-5xl">{getCampaignTypeIcon(campaign.campaign_type)}</div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-navy">{campaign.title}</h1>
                  <Badge className="mt-2">
                    {campaign.campaign_type.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </div>
              {user?.role === 'parent' && householdId && (
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowContributeModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Log Contribution
                </Button>
              )}
            </div>

            <p className="text-gray-700 mb-6">{campaign.description}</p>

            {/* Progress */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex justify-between items-end mb-2">
                <div>
                  <p className="text-3xl font-bold text-green-600">
                    ${campaign.current_amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">raised of ${campaign.goal_amount.toLocaleString()} goal</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-navy">{campaign.progress_percent?.toFixed(0) || 0}%</p>
                </div>
              </div>
              <Progress value={campaign.progress_percent || 0} className="h-4 mb-4" />
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="flex items-center justify-center text-gray-600 mb-1">
                    <Target className="w-4 h-4 mr-1" />
                  </div>
                  <p className="text-lg font-bold text-navy">${campaign.goal_amount.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Goal</p>
                </div>
                <div>
                  <div className="flex items-center justify-center text-gray-600 mb-1">
                    <Users className="w-4 h-4 mr-1" />
                  </div>
                  <p className="text-lg font-bold text-light-blue">{campaign.participation_count || 0}</p>
                  <p className="text-xs text-gray-500">Families</p>
                </div>
                <div>
                  <div className="flex items-center justify-center text-gray-600 mb-1">
                    <Clock className="w-4 h-4 mr-1" />
                  </div>
                  <p className={`text-lg font-bold ${campaign.days_remaining <= 3 ? 'text-red' : 'text-navy'}`}>
                    {campaign.days_remaining || 0}
                  </p>
                  <p className="text-xs text-gray-500">Days Left</p>
                </div>
              </div>

              {campaign.progress_percent >= 100 && (
                <div className="mt-4 p-4 bg-green-100 rounded-lg text-center">
                  <Trophy className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-bold text-lg">Goal Reached!</p>
                  <p className="text-green-600 text-sm">Thank you for your support!</p>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="flex flex-wrap gap-6 mt-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Started: {format(parseISO(campaign.start_date), 'MMMM d, yyyy')}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Ends: {format(parseISO(campaign.end_date), 'MMMM d, yyyy')}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="text-navy flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-gold" />
                Top Contributors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {participation.length > 0 ? (
                <div className="space-y-3">
                  {participation.slice(0, 10).map((p, idx) => (
                    <div 
                      key={p.participation_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-gold text-navy' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-navy'
                        }`}>
                          {idx + 1}
                        </div>
                        <span className="font-medium text-navy">{p.household_name || 'Anonymous'}</span>
                      </div>
                      <span className="font-bold text-green-600">${p.amount_raised?.toFixed(2) || '0.00'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No contributions yet</p>
                  <p className="text-sm">Be the first to contribute!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-navy flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contributions.length > 0 ? (
                <div className="space-y-3">
                  {contributions.slice(0, 10).map(c => (
                    <div 
                      key={c.contribution_id}
                      className="flex items-center justify-between p-3 border-l-4 border-green-500 bg-green-50/50 rounded-r-lg"
                    >
                      <div>
                        <p className="font-medium text-navy">${c.amount.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{c.notes || 'Contribution'}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {format(parseISO(c.created_at), 'MMM d')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No activity yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contribute Modal */}
        {showContributeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-navy">Log Contribution</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContribute} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={contributionAmount}
                      onChange={(e) => setContributionAmount(e.target.value)}
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <Input
                      value={contributionNotes}
                      onChange={(e) => setContributionNotes(e.target.value)}
                      placeholder="e.g., Sold 5 boxes of popcorn"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowContributeModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={submitting}
                    >
                      {submitting ? 'Recording...' : 'Record Contribution'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default CampaignDetailPage;
